// Shared helpers between CustomRoleEditor (view/edit of existing roles) and
// OrgRoleWizard (Sub-project E2, Task 6: the create-from-template wizard) —
// pulled out of CustomRoleEditor so both don't drift on the same behavior.

// Turn a display name into a stable role code: lowercase, ascii-ish,
// underscore-separated. Not meant to be bijective — it's just a friendly,
// internal identifier; the wizard doesn't expose it as an editable field.
export function slugify(input) {
  return (input || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);
}

// Map backend error codes (see backend_go cmd/api/roles_handlers.go /
// role_lifecycle_handlers.go) to messages a tenant admin can act on. Falls
// back to whatever message the server sent (e.g. ROLE_IN_USE includes the
// assigned-user count, ROLE_CONTROL_CHECK_FAILED already reads plainly),
// then to a generic action-specific message.
export function friendlyRoleError(err, action) {
  const code = err?.serverBody?.error?.code;
  const serverMessage = err?.serverBody?.error?.message;
  switch (code) {
    case "ROLE_IMMUTABLE":
      return "Platform and template roles can't be edited — use a template to create an organisation role.";
    case "ROLE_IN_USE":
      return serverMessage || "This role is currently assigned to one or more users and can't be deleted.";
    case "RESERVED_ROLE_CODE":
      return "That role code is reserved for a system role. Choose a different name.";
    case "DUPLICATE_ROLE_CODE":
      return "A role with a matching code already exists. Try a slightly different name.";
    case "INVALID_CAPABILITY":
      return "One or more selected capabilities aren't recognized. Refresh and try again.";
    case "ROLE_NOT_FOUND":
      return "This role no longer exists — it may have just been deleted.";
    case "ROLE_CONTROL_CHECK_FAILED":
      return serverMessage || "This role fails the control-check and can't be activated without an override reason.";
    case "LIFECYCLE_CONFLICT":
      return serverMessage || "This role's status changed before the transition could be applied — reload and try again.";
    case "INVALID_TRANSITION":
      return serverMessage || "That lifecycle transition isn't allowed from the role's current status.";
    default:
      return serverMessage || err?.message || `Failed to ${action} role`;
  }
}

// Row-level lifecycle actions (RolesCatalogPage's Organisation table) fire
// transitionRole() straight from the catalog — there's no override-reason
// affordance there (that lives in OrgRoleWizard's control-check step), so a
// 409 ROLE_CONTROL_CHECK_FAILED needs its own, action-appropriate copy
// instead of friendlyRoleError's wizard-flavored fallback ("...without an
// override reason").
export function friendlyTransitionError(err, action) {
  const code = err?.serverBody?.error?.code;
  if (code === "ROLE_CONTROL_CHECK_FAILED") {
    return "Can't activate — this role fails its control-check. Open the role to review it (the create wizard supports an override reason).";
  }
  return friendlyRoleError(err, action);
}

// Review column (RolesCatalogPage Organisation table) + role-detail summary:
// "Due in N days" / "Due today" / "Overdue" / "—" derived from
// `next_review_at`. Compared at day granularity (not exact ms) so a review
// due later today doesn't misreport as "Overdue".
export function reviewStatusLabel(nextReviewAt) {
  if (!nextReviewAt) return "—";
  const due = new Date(nextReviewAt);
  if (Number.isNaN(due.getTime())) return "—";
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(due) - startOfDay(new Date())) / (24 * 60 * 60 * 1000));
  if (diffDays < 0) return "Overdue";
  if (diffDays === 0) return "Due today";
  return `Due in ${diffDays} day${diffDays === 1 ? "" : "s"}`;
}

// Role-detail summary: review cadence in plain language, mirroring
// OrgRoleWizard's REVIEW_FREQUENCY_OPTIONS wording ("Every N days" /
// "No scheduled review") so the same role reads consistently whether you're
// creating or reviewing it.
export function reviewCadenceLabel(reviewFrequencyDays) {
  if (reviewFrequencyDays == null) return "No scheduled review";
  return `Every ${reviewFrequencyDays} day${reviewFrequencyDays === 1 ? "" : "s"}`;
}

// Plain calendar-date formatter for governance timestamps that render as a
// raw date rather than a relative label (e.g. the Archived tab's
// retirement date, from role.retired_at — internal/users.RoleDetail,
// stamped on the retire transition and serialized by GET /api/v1/roles).
// Day precision only (no time-of-day), falling back to "—" for
// missing/invalid input.
export function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}
